import { MemberInfoModal, LoginModal, UserManualModal } from "@components";
import { AuthContext, useAdminCompany } from "@context";
import { useContext, useState, useRef, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import { FaUser } from "react-icons/fa";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, LogOut, User } from "lucide-react";
import useMyMenus from "@hooks/comm/useMyMenus";
import positionDropdown from "@utils/dropdownPosition";

const CHILD_BG = '#ffffff';

function alignChildMenu(parentEl, childRowEl, minGap = 8) {
  const items = childRowEl.querySelectorAll('[data-child-item]');
  if (!items.length) return;

  let totalWidth = 0;
  items.forEach(el => { totalWidth += el.getBoundingClientRect().width; });

  const parentRect = parentEl.getBoundingClientRect();
  const parentCenter = parentRect.left + parentRect.width / 2;
  const half = totalWidth / 2;
  const vw = window.innerWidth;

  // 부모 중앙 기준으로 좌우대칭 시작점 계산
  let startX = parentCenter - half;

  // 왼쪽으로 넘치면 왼쪽 경계에 맞게 보정
  if (startX < minGap) {
    startX = minGap;
  }

  // 오른쪽으로 넘치면 오른쪽 경계에 맞게 보정
  if (startX + totalWidth > vw - minGap) {
    startX = vw - minGap - totalWidth;
  }

  // 보정 후에도 왼쪽 경계 침범하면 전체 너비 균등 분배로 fallback
  if (startX < minGap) {
    childRowEl.style.justifyContent = 'space-between';
    childRowEl.style.paddingLeft    = '16px';
    childRowEl.style.paddingRight   = '16px';
    return;
  }

  childRowEl.style.justifyContent = 'flex-start';
  childRowEl.style.paddingLeft    = startX + 'px';
  childRowEl.style.paddingRight   = '0px';
}

const Header = ({ activeMenu, onMenuSelect, toggleMobileMenu, isMobile, isSidebarOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAiAdminActive = location.pathname.startsWith("/ai/admin");

  const [showLogout, setShowLogout] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const { auth, logout } = useContext(AuthContext);
  const { setAdminCompCode, setAdminCompName } = useAdminCompany();
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);

  // 유저 드롭다운 refs
  const dropdownRef = useRef(null);
  const userButtonRef = useRef(null);
  const userMenuRef = useRef(null);

  // 네비게이션 state — hover + click 이중 상태
  const [hoveredMenu, setHoveredMenu] = useState(null);
  const [fixedMenu, setFixedMenu] = useState(null);
  const parentRefs = useRef({});
  const childRowRef = useRef(null);
  const headerRef = useRef(null);

  // 활성 메뉴: 고정(클릭) 우선, 없으면 호버
  const activeMenuId = fixedMenu ?? hoveredMenu;

  const { data: myMenus = [] } = useMyMenus(auth.isAuthenticated);

  const normalizeMenuId = (id) => String(id ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");

  const aiAdminSubmenus = [
    { menuCode: "AI_ADMIN_DASHBOARD", menuName: "대시보드", path: "/ai/admin" },
    { menuCode: "AI_ADMIN_INQUIRIES", menuName: "질의현황", path: "/ai/admin/inquiries" },
    { menuCode: "AI_ADMIN_QUALITY", menuName: "품질관리", path: "/ai/admin/console" },
  ];

  const dynamicMenus = myMenus.map((menu) => ({
    id: menu.menuCode.toLowerCase(),
    title: menu.menuName,
    path: menu.path,
    menuCode: menu.menuCode,
  }));

  // activeMenuId 로부터 자식 메뉴 도출
  const activeMenuData = activeMenuId
    ? myMenus.find(m => m.menuCode.toLowerCase() === activeMenuId)
    : null;
  const isActiveAiAdmin = activeMenuId ? normalizeMenuId(activeMenuId) === "aiadmin" : false;
  const activeChildren = activeMenuId
    ? (isActiveAiAdmin ? aiAdminSubmenus : (activeMenuData?.children ?? []))
    : [];
  const isPanelOpen = activeMenuId !== null && activeChildren.length > 0;

  const handleLogoutShow = () => setShowLogout(true);
  const handleLogoutClose = () => setShowLogout(false);
  const handleLogout = () => {
    setAdminCompCode("");
    setAdminCompName("");
    logout();
    handleLogoutClose();
  };

  // 라우트 변경 시 패널 닫기
  useEffect(() => {
    setFixedMenu(null);
    setHoveredMenu(null);
  }, [location.pathname]);

  // 헤더 외부 클릭 시 패널 닫기
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (headerRef.current && !headerRef.current.contains(e.target)) {
        setFixedMenu(null);
        setHoveredMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 유저 드롭다운 외부 클릭 닫기
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setUserDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 유저 드롭다운 위치 조정
  useEffect(() => {
    if (!userDropdownOpen) return;
    const rafId = requestAnimationFrame(() => {
      if (userButtonRef.current && userMenuRef.current) {
        positionDropdown(userButtonRef.current, userMenuRef.current);
      }
    });
    const handleResize = () => {
      if (userButtonRef.current && userMenuRef.current) {
        positionDropdown(userButtonRef.current, userMenuRef.current);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', handleResize);
    };
  }, [userDropdownOpen]);

  // 자식 메뉴 정렬 — 부모 중앙 기준 좌우대칭
  useEffect(() => {
    if (!isPanelOpen || !activeMenuId) return;
    const doAlign = () => {
      const cr = childRowRef.current;
      const pe = parentRefs.current[activeMenuId];
      if (cr && pe) alignChildMenu(pe, cr);
    };
    const rafId = requestAnimationFrame(doAlign);
    window.addEventListener('resize', doAlign);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', doAlign);
      const cr = childRowRef.current;
      if (cr) {
        cr.style.justifyContent = '';
        cr.style.paddingLeft = '';
        cr.style.paddingRight = '';
      }
    };
  }, [isPanelOpen, activeMenuId]);

  return (
    <>
      <header
        ref={headerRef}
        onMouseLeave={() => setHoveredMenu(null)}
        className={`sticky top-0 z-50 flex-none shadow-[0_2px_12px_rgba(0,0,0,0.06)] relative
          ${isPanelOpen ? '' : 'border-b-2 border-gray-200'}`}
        style={{ background: '#FBFBFB' }}
      >
        {/* 메인 헤더 바 — items-stretch 로 버튼이 전체 높이를 채워 ㅗ 연결 */}
        <div className="h-[72px] px-4 md:px-6 xl:px-8 flex items-stretch justify-between gap-6"
          style={isPanelOpen ? { borderBottom: '1px solid #e5e7eb' } : {}}>

          {/* 로고 */}
          <Link to="/main" className="flex items-center gap-2.5 no-underline shrink-0">
            <span
              className="font-extrabold tracking-tight text-gray-900 truncate max-w-[120px] sm:max-w-[200px] lg:max-w-none"
              style={{ fontSize: "clamp(14px, 1.6vw, 26px)" }}
            >
              {auth.isAuthenticated && auth.compName ? auth.compName : "중대 재해"}
            </span>
          </Link>

          {/* 중앙 메인 메뉴 */}
          {!isMobile && (
            <nav className="flex-1 min-w-0 flex items-stretch">
              {dynamicMenus.map((menu) => {
                const normalizedMenuId = normalizeMenuId(menu.id);
                const isAiAdminMenu = normalizedMenuId === "aiadmin";
                const isAiMenu = normalizedMenuId === "ai";

                const menuData = myMenus.find(m => m.menuCode.toLowerCase() === menu.id);
                const children = isAiAdminMenu ? aiAdminSubmenus : (menuData?.children ?? []);

                const isChildActive = children.some(child => {
                  const to = child.path || child.url || child.menuUrl || '';
                  return to && (location.pathname === to || location.pathname.startsWith(to + '/'));
                });
                const isDirectActive = menu.path ? location.pathname === menu.path : false;

                const isRouteActive = activeMenu
                  ? normalizeMenuId(activeMenu) === normalizedMenuId
                  : (isAiAdminMenu ? isAiAdminActive
                    : isAiMenu ? location.pathname.startsWith("/ai") && !isAiAdminActive
                      : isChildActive || isDirectActive);

                const isPanelActiveForThis = activeMenuId === menu.id;
                // 패널이 닫혀 있고 URL 활성인 경우만 밑줄 표시
                const showUnderline = !isPanelActiveForThis && isRouteActive;

                return (
                  <div
                    key={menu.id}
                    className="flex-1 flex items-stretch"
                    style={isPanelActiveForThis && isPanelOpen ? { marginBottom: '-1px' } : {}}
                  >
                    <button
                      ref={el => { parentRefs.current[menu.id] = el; }}
                      onMouseEnter={() => setHoveredMenu(menu.id)}
                      onClick={() => {
                        if (children.length === 0 && menu.path) {
                          navigate(menu.path);
                          setFixedMenu(null);
                          setHoveredMenu(null);
                        } else {
                          setFixedMenu(prev => prev === menu.id ? null : menu.id);
                        }
                      }}
                      className={`relative w-full font-bold whitespace-nowrap transition-colors duration-150 rounded-none
                        ${isPanelActiveForThis || isRouteActive ? 'text-blue-600' : 'text-gray-400 hover:text-gray-600'}
                        ${showUnderline ? 'after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[3px] after:bg-blue-600 after:rounded-t' : ''}`}
                      style={{
                        fontSize: '14px',
                        paddingLeft: '16px',
                        paddingRight: '16px',
                        borderRadius: 0,
                        ...(isPanelActiveForThis && isPanelOpen ? {
                          background: '#FFFFFF',
                          borderTop: '1px solid #e5e7eb',
                          borderLeft: '1px solid #e5e7eb',
                          borderRight: '1px solid #e5e7eb',
                          borderBottom: 'none',
                        } : {
                          background: 'transparent',
                          border: 'none',
                        }),
                      }}
                    >
                      {menu.title}
                    </button>
                  </div>
                );
              })}
            </nav>
          )}

          {/* 유저 영역 + 모바일 햄버거 */}
          <div className="flex items-center gap-2 shrink-0">
            {auth.isAuthenticated ? (
              <div className="flex items-center gap-2" style={{ alignItems: 'center' }}>
                <button
                  onClick={() => setManualOpen(true)}
                  title="사용자 설명서"
                  onMouseEnter={e => e.currentTarget.style.background = '#f3f4f6'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  style={{
                    width: '30px',
                    height: '30px',
                    minWidth: '30px',
                    minHeight: '30px',
                    borderRadius: '50%',
                    border: 'none',
                    background: 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px',
                    fontWeight: '700',
                    color: '#6b7280',
                    cursor: 'pointer',
                    padding: '0',
                    lineHeight: '1',
                    boxSizing: 'border-box',
                    flexShrink: '0',
                    alignSelf: 'center',
                    aspectRatio: '1 / 1',
                    transition: 'background 0.15s',
                  }}
                >
                  ?
                </button>
                <div className="relative" ref={dropdownRef}>
                  <button
                    ref={userButtonRef}
                    onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                    className={`flex items-center gap-2 px-2 lg:px-3 py-1.5 rounded-lg border transition-colors duration-150
                      ${userDropdownOpen ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-white hover:bg-gray-50'}`}
                  >
                    <div className="bg-gray-100 rounded-full text-gray-500 flex items-center justify-center shrink-0 w-[30px] h-[30px] lg:w-[32px] lg:h-[32px]">
                      <FaUser size={13} />
                    </div>
                    <span className="font-bold text-gray-700 hidden lg:block" style={{ fontSize: "clamp(14px, 1.1vw, 18px)" }}>
                      {auth.name || "사용자"}
                    </span>
                  </button>

                  {userDropdownOpen && (
                    <div ref={userMenuRef} className="absolute right-0 top-[calc(100%+8px)] w-full min-w-[120px] lg:min-w-[160px] bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-50">
                      <button
                        data-dd-item
                        onClick={() => { setFormModalOpen(true); setUserDropdownOpen(false); }}
                        className="w-full text-left px-3 lg:px-4 py-2 lg:py-3 text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors"
                        style={{ fontSize: "clamp(13px, 1vw, 17px)" }}
                      >
                        <User size={isMobile ? 15 : 18} className="text-gray-400" /> 내정보
                      </button>
                      <div className="h-px bg-gray-100" />
                      <button
                        data-dd-item
                        onClick={() => { handleLogoutShow(); setUserDropdownOpen(false); }}
                        className="w-full text-left px-3 lg:px-4 py-2 lg:py-3 text-red-500 hover:bg-red-50 flex items-center gap-2 font-bold transition-colors"
                        style={{ fontSize: "clamp(13px, 1vw, 17px)" }}
                      >
                        <LogOut size={isMobile ? 15 : 18} /> 로그아웃
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <button
                onClick={() => setLoginModalOpen(true)}
                className="px-5 py-2 rounded-lg bg-blue-600 text-white font-bold hover:bg-blue-700 transition-colors"
                style={{ fontSize: "clamp(14px, 1.1vw, 18px)" }}
              >
                로그인
              </button>
            )}

            {isMobile && (
              <button onClick={toggleMobileMenu} className="p-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg ml-1">
                <Menu size={26} />
              </button>
            )}
          </div>
        </div>

        {/* 자식 메뉴 패널 */}
        <div
          className={`absolute left-0 right-0 top-full z-[9998] border-b border-gray-200
            transition-[opacity,transform] duration-150 ease-out
            ${isPanelOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-1 pointer-events-none'}`}
          style={{ backgroundColor: CHILD_BG }}
        >
          <div
            ref={childRowRef}
            className="flex flex-nowrap items-center py-3"
            style={{ transition: 'padding-left 0.15s ease' }}
          >
            {activeChildren.map(child => {
              const to = child.path || child.url || child.menuUrl || '/';
              const isSubActive = location.pathname === to;
              return (
                <Link
                  key={child.menuCode}
                  data-child-item
                  to={to}
                  onClick={() => {
                    onMenuSelect(activeMenuId);
                    setFixedMenu(null);
                    setHoveredMenu(null);
                  }}
                  style={{ fontSize: '13px', paddingLeft: '14px', paddingRight: '14px' }}
                  className={`relative whitespace-nowrap py-2 rounded-md font-medium text-center transition-colors duration-150 no-underline
                    ${isSubActive
                      ? 'text-blue-600 font-bold bg-blue-100 after:absolute after:bottom-1 after:left-3 after:right-3 after:h-[2px] after:rounded-full after:bg-blue-600'
                      : 'text-gray-500 hover:text-blue-600'}`}
                >
                  {child.menuName}
                </Link>
              );
            })}
          </div>
        </div>
      </header>

      {/* 모달 */}
      <Modal show={showLogout} onHide={handleLogoutClose} centered>
        <Modal.Header className="border-b-0 pb-0">
          <Modal.Title className="font-bold text-gray-800">로그아웃 하시겠습니까?</Modal.Title>
          <button type="button" className="modal-btn-close" onClick={handleLogoutClose}>
            <span className="modal-btn-span">X</span>
          </button>
        </Modal.Header>
        <Modal.Body className="text-gray-600 pt-2 pb-4">로그아웃 선택 시 현재 세션이 종료됩니다.</Modal.Body>
        <Modal.Footer className="border-t-0 pt-0">
          <Button variant="light" className="bg-gray-100 border-0" onClick={handleLogoutClose}>취소</Button>
          <Button variant="primary" className="bg-blue-600 border-0" onClick={handleLogout}>로그아웃</Button>
        </Modal.Footer>
      </Modal>

      <MemberInfoModal isOpen={formModalOpen} onClose={() => setFormModalOpen(false)} />
      <LoginModal isOpen={loginModalOpen} onClose={() => setLoginModalOpen(false)} />
      <UserManualModal isOpen={manualOpen} onClose={() => setManualOpen(false)} />
    </>
  );
};

export default Header;
